// ============================================================================
// TIMESHEET ENTRY POPUP
// ============================================================================
// Small modal (modal-md) for adding/editing a single timesheet entry row.
// Follows the prima-web form-modal blueprint:
//  - Internal formData state
//  - Inline field validation via fieldErrors
//  - Time input with flexible format parsing (8 -> 480min, 8.5 -> 510min, 8:30 -> 510min)
//  - No minimize support (small action popup)
//
// Week starts on WEDNESDAY: Wed, Thu, Fri, Sat, Sun, Mon, Tue

import React, { useState, useCallback, useMemo, useEffect } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { SyutSelect } from 'components/ui';
import { SyutEngagementSelect, SyutActivitySelect } from 'components/ui/SyutEntitySelect';
import { engagementService } from 'api/services/engagementService';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface TimesheetEntryPopupProps {
  isOpen: boolean;
  mode: 'create' | 'edit';
  initialData?: {
    chargeTypeEnumId: string;
    engagementId?: string;
    activityId: string;
    remarks?: string;
    dayMinutes: Record<string, number>;
  } | null;
  weekDates: string[];           // 7 dates [Wed..Tue]
  holidays: string[];
  chargeTypeOptions: { value: string; label: string; key: string }[];
  /**
   * Seed options used to resolve the saved engagement_id's label in edit mode.
   * The dropdown itself is async — server-search after 3 chars typed — so the
   * seed only needs to cover the rows referenced by EXISTING entries on this
   * timesheet (i.e. small).
   */
  engagementOptions: { value: string; label: string }[];
  /**
   * Seed options used to resolve the saved activity_id's label in edit mode.
   * The dropdown itself is async — server-search after 3 chars typed — and
   * carries chargeType/engagementType filters so results stay relevant.
   */
  activityOptions: { value: string; label: string; chargeTypeKey: string; engagementTypeIds?: string[] }[];
  readOnly?: boolean;
  onSave: (data: {
    chargeTypeEnumId: string;
    engagementId?: string;
    activityId: string;
    remarks?: string;
    dayMinutes: Record<string, number>;
  }) => void;
  onClose: () => void;
}

// ---------------------------------------------------------------------------
// Time helpers
// ---------------------------------------------------------------------------

function parseTimeInput(input: string): number {
  if (!input || input === '\u2014' || input === '-') return 0;
  const trimmed = input.trim();
  if (trimmed.includes(':')) {
    const [h, m] = trimmed.split(':').map(Number);
    return (h || 0) * 60 + (m || 0);
  }
  const num = parseFloat(trimmed);
  if (isNaN(num)) return 0;
  return Math.round(num * 60);
}

export function formatMinutes(minutes: number): string {
  if (!minutes || minutes <= 0) return '—';
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return `${h}:${String(m).padStart(2, '0')}`;
}

function formatDateLabel(dateStr: string, idx: number): string {
  const dayLabels = ['Wed', 'Thu', 'Fri', 'Sat', 'Sun', 'Mon', 'Tue'];
  const d = new Date(dateStr + 'T00:00:00');
  return `${dayLabels[idx]} ${String(d.getDate()).padStart(2, '0')}`;
}

// ---------------------------------------------------------------------------
// Initial form state
// ---------------------------------------------------------------------------

interface FormData {
  chargeTypeEnumId: string;
  engagementId: string;
  activityId: string;
  remarks: string;
  dayInputs: Record<string, string>; // date -> display string (editable)
}

const INITIAL_FORM: FormData = {
  chargeTypeEnumId: '',
  engagementId: '',
  activityId: '',
  remarks: '',
  dayInputs: {},
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export const TimesheetEntryPopup: React.FC<TimesheetEntryPopupProps> = ({
  isOpen,
  mode,
  initialData,
  weekDates,
  holidays,
  chargeTypeOptions,
  engagementOptions,
  activityOptions,
  readOnly = false,
  onSave,
  onClose,
}) => {
  const [formData, setFormData] = useState<FormData>({ ...INITIAL_FORM });
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  const holidaySet = useMemo(() => new Set(holidays), [holidays]);

  // Populate form on open
  useEffect(() => {
    if (!isOpen) return;
    if (initialData) {
      const dayInputs: Record<string, string> = {};
      weekDates.forEach(date => {
        const mins = initialData.dayMinutes[date] || 0;
        dayInputs[date] = mins > 0 ? formatMinutes(mins) : '';
      });
      setFormData({
        chargeTypeEnumId: initialData.chargeTypeEnumId || '',
        engagementId: initialData.engagementId || '',
        activityId: initialData.activityId || '',
        remarks: initialData.remarks || '',
        dayInputs,
      });
      // For edit mode, fetch the engagement's type so activity filter applies.
      if (initialData.engagementId) {
        engagementService
          .getById(initialData.engagementId)
          .then(res => setSelectedEngagementTypeId((res?.data as any)?.engagementTypeId || ''))
          .catch(() => setSelectedEngagementTypeId(''));
      } else {
        setSelectedEngagementTypeId('');
      }
    } else {
      const dayInputs: Record<string, string> = {};
      weekDates.forEach(date => { dayInputs[date] = ''; });
      setFormData({ ...INITIAL_FORM, dayInputs });
      setSelectedEngagementTypeId('');
    }
    setFieldErrors({});
  }, [isOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  // Selected charge type key
  const selectedChargeTypeKey = useMemo(() => {
    const opt = chargeTypeOptions.find(o => o.value === formData.chargeTypeEnumId);
    return opt?.key || '';
  }, [formData.chargeTypeEnumId, chargeTypeOptions]);

  const showEngagement = selectedChargeTypeKey === 'C';

  // Engagement type of the currently selected engagement (for activity filtering).
  // Fetched on demand from engagementService.getById when engagement changes.
  const [selectedEngagementTypeId, setSelectedEngagementTypeId] = useState<string>('');

  // Activity filter parameters — passed straight through to SyutActivitySelect's
  // server-side loadOptions. The server narrows by charge type key for all
  // entries, and by engagement_type_id additionally when the row is Chargeable
  // (C) and an engagement is selected.
  const activityChargeTypeFilter = selectedChargeTypeKey || undefined;
  const activityEngagementTypeFilter =
    selectedChargeTypeKey === 'C' && selectedEngagementTypeId ? selectedEngagementTypeId : undefined;

  // Row total
  const rowTotal = useMemo(() => {
    return weekDates.reduce((sum, date) => sum + parseTimeInput(formData.dayInputs[date] || ''), 0);
  }, [weekDates, formData.dayInputs]);

  // Clear field error
  const clearFieldError = useCallback((field: string) => {
    setFieldErrors(prev => {
      if (!prev[field]) return prev;
      const next = { ...prev };
      delete next[field];
      return next;
    });
  }, []);

  const getFieldError = useCallback((field: string) => fieldErrors[field], [fieldErrors]);

  // Handlers
  const handleChargeTypeChange = useCallback((val: string | string[] | null) => {
    clearFieldError('chargeTypeEnumId');
    setFormData(prev => ({
      ...prev,
      chargeTypeEnumId: (val as string) || '',
      engagementId: '',  // reset engagement on type change
      activityId: '',    // reset activity on type change
    }));
    setSelectedEngagementTypeId('');
  }, [clearFieldError]);

  const handleEngagementChange = useCallback(async (newEngagementId: string) => {
    clearFieldError('engagementId');
    setFormData(prev => ({
      ...prev,
      engagementId: newEngagementId,
      activityId: '',  // reset activity — it may no longer match the new engagement's type
    }));
    // Fetch the engagement's type so activity filtering can narrow the list.
    if (newEngagementId) {
      try {
        const res = await engagementService.getById(newEngagementId);
        setSelectedEngagementTypeId((res?.data as any)?.engagementTypeId || '');
      } catch {
        setSelectedEngagementTypeId('');
      }
    } else {
      setSelectedEngagementTypeId('');
    }
  }, [clearFieldError]);

  const handleActivityChange = useCallback((val: string | string[] | null) => {
    clearFieldError('activityId');
    setFormData(prev => ({ ...prev, activityId: (val as string) || '' }));
  }, [clearFieldError]);

  const handleDayInputChange = useCallback((date: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      dayInputs: { ...prev.dayInputs, [date]: value },
    }));
  }, []);

  const handleDayInputBlur = useCallback((date: string) => {
    setFormData(prev => {
      const raw = prev.dayInputs[date] || '';
      const mins = parseTimeInput(raw);
      return {
        ...prev,
        dayInputs: { ...prev.dayInputs, [date]: mins > 0 ? formatMinutes(mins) : '' },
      };
    });
  }, []);

  // Validate & save
  const handleSave = useCallback(() => {
    if (readOnly) return;

    const errors: Record<string, string> = {};
    if (!formData.chargeTypeEnumId) errors.chargeTypeEnumId = 'Charge type is required';
    if (showEngagement && !formData.engagementId) errors.engagementId = 'Engagement is required';
    if (!formData.activityId) errors.activityId = 'Activity is required';

    // At least one day must have hours
    const totalMins = weekDates.reduce((sum, date) => sum + parseTimeInput(formData.dayInputs[date] || ''), 0);
    if (totalMins <= 0) errors.hours = 'Enter hours for at least one day';

    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      return;
    }

    setFieldErrors({});

    // Build dayMinutes
    const dayMinutes: Record<string, number> = {};
    weekDates.forEach(date => {
      const mins = parseTimeInput(formData.dayInputs[date] || '');
      if (mins > 0) dayMinutes[date] = mins;
    });

    onSave({
      chargeTypeEnumId: formData.chargeTypeEnumId,
      engagementId: showEngagement ? formData.engagementId : undefined,
      activityId: formData.activityId,
      remarks: formData.remarks || undefined,
      dayMinutes,
    });
  }, [formData, showEngagement, weekDates, readOnly, onSave]);

  if (!isOpen) return null;

  return (
    <>
      <div className="modal-backdrop" />
      <div className="modal-container modal-md">
        {/* Header */}
        <div className="modal-header" style={{ minHeight: 'auto' }}>
          <h2 className="modal-title">
            {mode === 'create' ? 'Add Timesheet Entry' : 'Edit Timesheet Entry'}
          </h2>
          <button className="modal-close" onClick={onClose} title="Close">
            <IconSystem.x size={20} />
          </button>
        </div>

        {/* Body */}
        <div className="modal-content">
          {/* Read-only warning */}
          {readOnly && (
            <div style={{
              padding: '0.5rem 0.75rem',
              marginBottom: '0.75rem',
              background: '#fef3c7',
              border: '1px solid #fde68a',
              borderRadius: '4px',
              color: '#92400e',
              fontSize: '0.8125rem',
            }}>
              This engagement has been closed. Entry cannot be modified.
            </div>
          )}

          {/* Row 1: Charge Type */}
          <div className="form-group" data-field="chargeTypeEnumId">
            <SyutSelect
              label={<>Charge Type <span className="text-danger">*</span></>}
              value={formData.chargeTypeEnumId || undefined}
              options={chargeTypeOptions}
              onChange={handleChargeTypeChange}
              isClearable
              size="sm"
              isDisabled={readOnly}
              error={getFieldError('chargeTypeEnumId')}
            />
          </div>

          {/* Row 2: Engagement (only for Chargeable) */}
          {showEngagement && (
            <div className="form-group" data-field="engagementId">
              <SyutEngagementSelect
                label={<>Engagement <span className="text-danger">*</span></>}
                value={formData.engagementId || undefined}
                seedOptions={engagementOptions}
                onChange={handleEngagementChange}
                disabled={readOnly}
                error={getFieldError('engagementId')}
                placeholder="Search by client, type, or year..."
              />
            </div>
          )}

          {/* Row 3: Activity — async server-search.
              The server-side filter narrows by charge type (and by
              engagement type for chargeable rows) so the user sees a
              relevant subset without having to scroll the entire master. */}
          <div className="form-group" data-field="activityId">
            <SyutActivitySelect
              label={<>Activity <span className="text-danger">*</span></>}
              value={formData.activityId || undefined}
              seedOptions={activityOptions}
              chargeTypeKey={activityChargeTypeFilter}
              engagementTypeId={activityEngagementTypeFilter}
              onChange={(val) => handleActivityChange(val || null)}
              isClearable
              disabled={readOnly}
              error={getFieldError('activityId')}
              placeholder="Type 3 letters to search activities..."
            />
          </div>

          {/* Row 4: Remarks */}
          <div className="form-group">
            <label className="form-label">Remarks</label>
            <input
              type="text"
              className="form-input"
              value={formData.remarks}
              maxLength={500}
              disabled={readOnly}
              placeholder="Optional remarks..."
              onChange={(e) => setFormData(prev => ({ ...prev, remarks: e.target.value }))}
              style={{ fontSize: '0.8125rem', padding: '0.375rem 0.625rem' }}
            />
          </div>

          {/* Row 5: Day hours */}
          <div style={{ marginTop: '0.75rem' }}>
            <label className="form-label" style={{ marginBottom: '0.375rem' }}>
              Hours <span className="text-danger">*</span>
            </label>

            {fieldErrors.hours && (
              <div className="form-error-message" style={{ marginBottom: '0.375rem' }}>
                {fieldErrors.hours}
              </div>
            )}

            <div style={{
              display: 'flex',
              gap: '4px',
              alignItems: 'stretch',
            }}>
              {weekDates.map((date, idx) => {
                const isHoliday = holidaySet.has(date);
                return (
                  <div key={date} style={{
                    flex: 1,
                    textAlign: 'center',
                    minWidth: 0,
                  }}>
                    <div style={{
                      fontSize: '0.7rem',
                      fontWeight: 600,
                      color: 'var(--text-secondary)',
                      marginBottom: '0.25rem',
                    }}>
                      {formatDateLabel(date, idx)}
                      {isHoliday && <span style={{ marginLeft: 2 }} title="Holiday">{'\uD83C\uDFD6\uFE0F'}</span>}
                    </div>
                    <input
                      type="text"
                      className="form-input"
                      value={formData.dayInputs[date] || ''}
                      disabled={readOnly}
                      placeholder="0:00"
                      onChange={(e) => handleDayInputChange(date, e.target.value)}
                      onBlur={() => handleDayInputBlur(date)}
                      style={{
                        fontSize: '0.8125rem',
                        padding: '0.375rem 0.25rem',
                        textAlign: 'center',
                        width: '100%',
                        background: isHoliday ? 'rgba(100,116,139,0.06)' : undefined,
                      }}
                    />
                  </div>
                );
              })}

              {/* Row total */}
              <div style={{
                width: '64px',
                flexShrink: 0,
                textAlign: 'center',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'flex-end',
              }}>
                <div style={{
                  fontSize: '0.7rem',
                  fontWeight: 600,
                  color: 'var(--text-secondary)',
                  marginBottom: '0.25rem',
                }}>
                  Total
                </div>
                <div style={{
                  fontSize: '0.875rem',
                  fontWeight: 700,
                  padding: '0.375rem 0.25rem',
                  color: 'var(--text-primary)',
                }}>
                  {formatMinutes(rowTotal)}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="modal-footer" style={{ minHeight: 'auto' }}>
          <button className="btn btn-secondary" onClick={onClose}>
            Cancel
          </button>
          {!readOnly && (
            <button className="btn btn-primary" onClick={handleSave}>
              {mode === 'create' ? 'Add to Timesheet' : 'Update Entry'}
            </button>
          )}
        </div>
      </div>
    </>
  );
};

export default TimesheetEntryPopup;
